import { isValidIBAN } from 'ibantools';
import { z } from 'zod';

import { GENDER_VALUES, type GenderValue } from '@kit/shared/gender';

import { phoneE164Field } from '../lib/phone-e164';
import { optionalProfileHandleSchema } from './handle.schema';

/**
 * Helper function to validate URLs
 */
function isValidUrl(str: string): boolean {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
}

/**
 * A numeric field a form can CLEAR. An empty `<input type="number">` read with
 * `valueAsNumber: true` yields `NaN`, which `z.number().min(...)` rejects —
 * blocking the save instead of clearing the field. This preprocess maps `NaN`
 * / `''` / `null` to an explicit `null` (a first-class "cleared" state), then
 * validates a nullable number so min/max only apply to real values. The mapper
 * turns that `null` into a JSON null so the RPC nulls the column; `undefined`
 * (untouched) still omits. `.optional()` keeps untouched fields undefined.
 */
function clearableNumber(schema: z.ZodNumber) {
  return z
    .preprocess(
      (v) =>
        v === '' || v === null || (typeof v === 'number' && Number.isNaN(v))
          ? null
          : v,
      schema.nullable(),
    )
    .optional();
}

/**
 * Normalize a social-handle input from the wizard. Users routinely paste
 * full profile URLs (the placeholder for Facebook/YouTube literally invites
 * it), so the regex-based validators below were rejecting every URL paste
 * with "Invalid <platform> handle format" (Sentry BALLEE-CN).
 *
 * Extracts the handle from common URL shapes per platform, strips a leading
 * "@", trims, and returns `undefined` for empty strings so the field stays
 * optional. Special-cases Facebook's `profile.php?id=NUMERIC` by returning
 * the numeric ID.
 */
type SocialPlatform =
  | 'instagram'
  | 'tiktok'
  | 'facebook'
  | 'youtube'
  | 'vimeo'
  | 'pinterest'
  | 'snapchat'
  | 'x';

export function normalizeSocialHandle(
  val: string,
  platform: SocialPlatform,
): string | undefined {
  const trimmed = val.trim();
  if (trimmed === '') return undefined;

  // Try URL extraction first
  let candidate = trimmed;
  try {
    const url = new URL(
      trimmed.startsWith('http') ? trimmed : `https://${trimmed}`,
    );
    const host = url.hostname.toLowerCase();
    const matchesPlatformHost = (() => {
      switch (platform) {
        case 'instagram':
          return /(^|\.)instagram\.com$/.test(host);
        case 'tiktok':
          return /(^|\.)tiktok\.com$/.test(host);
        case 'facebook':
          return /(^|\.)(facebook|fb)\.com$/.test(host);
        case 'youtube':
          return /(^|\.)(youtube\.com|youtu\.be)$/.test(host);
        case 'vimeo':
          return /(^|\.)vimeo\.com$/.test(host);
        case 'pinterest':
          return /(^|\.)pinterest\.[a-z.]+$/.test(host);
        case 'snapchat':
          return /(^|\.)snapchat\.com$/.test(host);
        case 'x':
          return /(^|\.)(twitter\.com|x\.com)$/.test(host);
      }
    })();

    if (matchesPlatformHost) {
      // Facebook profile.php?id=N → numeric ID
      if (
        platform === 'facebook' &&
        url.pathname.replace(/\/+$/, '') === '/profile.php'
      ) {
        const id = url.searchParams.get('id');
        if (id) candidate = id;
      } else {
        // Take the last non-empty path segment
        const segments = url.pathname.split('/').filter(Boolean);
        // YouTube /channel/UC... and /c/<name> and /@handle
        if (platform === 'youtube' && segments.length >= 2) {
          candidate = segments[segments.length - 1] ?? trimmed;
        } else if (segments.length > 0) {
          candidate = segments[segments.length - 1] ?? trimmed;
        }
      }
    }
  } catch {
    // Not a URL — fall through with the trimmed value
  }

  // Strip leading @ (the wizard input also strips, but server-side payloads
  // can include it)
  if (candidate.startsWith('@')) candidate = candidate.slice(1);

  return candidate === '' ? undefined : candidate;
}

/**
 * Personal Information Schema
 * Handles basic personal details for dancer profiles
 */
export const PersonalInfoSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(100, 'First name too long'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(100, 'Last name too long'),
  dateOfBirth: z
    .date()
    .max(new Date(), 'Date of birth cannot be in the future')
    .min(new Date('1900-01-01'), 'Invalid date of birth')
    .optional(),
  // Canonical gender_type values from @kit/shared/gender (BAL-374) — no
  // parallel literal list (Rule #8).
  gender: z.enum(GENDER_VALUES as [GenderValue, ...GenderValue[]], {
    required_error: 'Please select your gender',
  }),
  phoneNumber: phoneE164Field.optional(),
  profilePhotoUrl: z
    .string()
    .url()
    .refine(
      (url) => url.startsWith('https://'),
      'Profile photo URL must use HTTPS',
    )
    .optional(),
  nationalities: z
    .array(z.string().length(2, 'Invalid country code'))
    .max(10, 'Maximum 10 nationalities')
    .optional(),
  countryOfResidence: z.string().length(2, 'Invalid country code').optional(),
  languages: z
    .array(z.string().min(2, 'Invalid language code').max(10))
    .max(20, 'Maximum 20 languages')
    .optional(),
});

/**
 * Address Schema
 * Handles complete address information
 */
export const AddressSchema = z.object({
  streetAddress: z.string().optional(),
  apartment: z.string().optional(),
  city: z.string().optional(),
  stateProvince: z.string().optional(),
  postalCode: z.string().optional(),
  countryCode: z.string().length(2).optional(),
  // Google Places `place_id` captured when the dancer picks a suggestion.
  // Opaque to us — kept for future normalization / geocoding without re-querying.
  placeId: z.string().max(200).optional(),
});

/**
 * Professional Information Schema
 * Handles career-related information for dancers
 */
export const ProfessionalInfoSchema = z.object({
  bio: z
    .string()
    .max(2000, 'Bio must be under 2000 characters')
    .transform((str) => str?.replace(/<[^>]*>/g, '')) // Strip HTML tags for security
    .optional(),
  yearsOfExperience: clearableNumber(z.number().int().min(0).max(50)),
  danceStyles: z
    .array(z.string().max(100))
    .max(10, 'Maximum 10 dance styles')
    .optional(),
  instagramHandle: z
    .string()
    .transform((val) => normalizeSocialHandle(val, 'instagram'))
    .optional()
    .refine(
      (val) => !val || /^[a-zA-Z0-9_.]+$/.test(val),
      'Invalid Instagram handle format',
    )
    .refine((val) => !val || val.length <= 30, 'Instagram handle too long'),
  tiktokHandle: z
    .string()
    .transform((val) => normalizeSocialHandle(val, 'tiktok'))
    .optional()
    .refine(
      (val) => !val || /^[a-zA-Z0-9_.]+$/.test(val),
      'Invalid TikTok handle format',
    )
    .refine((val) => !val || val.length <= 30, 'TikTok handle too long'),
  facebookHandle: z
    .string()
    .transform((val) => normalizeSocialHandle(val, 'facebook'))
    .optional()
    .refine(
      // Allow letters, digits, dots, hyphens (real handles) and pure-numeric
      // IDs extracted from `facebook.com/profile.php?id=N`.
      (val) => !val || /^[a-zA-Z0-9.-]+$/.test(val),
      'Invalid Facebook handle format',
    )
    .refine((val) => !val || val.length <= 50, 'Facebook handle too long'),
  youtubeHandle: z
    .string()
    .transform((val) => normalizeSocialHandle(val, 'youtube'))
    .optional()
    .refine(
      (val) => !val || /^[a-zA-Z0-9_-]+$/.test(val),
      'Invalid YouTube handle format',
    )
    .refine((val) => !val || val.length <= 30, 'YouTube handle too long'),
  // Phase 2 socials additions (Philippine 2026-05-12). Schema columns
  // added in migration 20260512100000. Same handle validation pattern
  // as the other socials.
  vimeoHandle: z
    .string()
    .transform((val) => normalizeSocialHandle(val, 'vimeo'))
    .optional()
    .refine(
      (val) => !val || /^[a-zA-Z0-9_-]+$/.test(val),
      'Invalid Vimeo handle format',
    )
    .refine((val) => !val || val.length <= 40, 'Vimeo handle too long'),
  pinterestHandle: z
    .string()
    .transform((val) => normalizeSocialHandle(val, 'pinterest'))
    .optional()
    .refine(
      (val) => !val || /^[a-zA-Z0-9_.-]+$/.test(val),
      'Invalid Pinterest handle format',
    )
    .refine((val) => !val || val.length <= 30, 'Pinterest handle too long'),
  snapchatHandle: z
    .string()
    .transform((val) => normalizeSocialHandle(val, 'snapchat'))
    .optional()
    .refine(
      (val) => !val || /^[a-zA-Z0-9_.-]+$/.test(val),
      'Invalid Snapchat handle format',
    )
    .refine((val) => !val || val.length <= 30, 'Snapchat handle too long'),
  xHandle: z
    .string()
    .transform((val) => normalizeSocialHandle(val, 'x'))
    .optional()
    .refine(
      (val) => !val || /^[a-zA-Z0-9_]+$/.test(val),
      'Invalid X handle format',
    )
    .refine((val) => !val || val.length <= 15, 'X handle too long (15 max)'),
  // Phase 2 brand / vision additions. Short free-text fields surfaced
  // on the public profile.
  brandAmbassador: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().max(500, 'Brand ambassador text under 500 chars'))
    .optional(),
  favoriteDanceBrand: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().max(100, 'Favourite dance brand under 100 chars'))
    .optional(),
  inspirations: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().max(300, 'Inspirations under 300 chars'))
    .optional(),
  artisticVision: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().max(300, 'Artistic vision under 300 chars'))
    .optional(),
  dreamCollaborations: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().max(300, 'Dream collaborations under 300 chars'))
    .optional(),
  portfolioUrl: z
    .string()
    .transform((val) => (val.trim() === '' ? undefined : val))
    .optional()
    .refine(
      (val) => !val || (val.startsWith('https://') && isValidUrl(val)),
      'Portfolio URL must be a valid HTTPS URL',
    ),
  // BAL-248 §3: "Company Website Profile Page" social link — a URL to the
  // dancer's profile page on their company's official site, distinct from
  // portfolioUrl / website. Column: professional_profiles.company_website.
  companyWebsite: z
    .string()
    .transform((val) => (val.trim() === '' ? undefined : val))
    .optional()
    .refine(
      (val) => !val || (val.startsWith('https://') && isValidUrl(val)),
      'Company website must be a valid HTTPS URL',
    ),
  // BAL-493: public stage/display name. Accepted by atomic_profile_update
  // (inside p_professional_data) + read on the public hero, but was absent
  // from the schema/mapper/editor — so it was unwritable via the action.
  // Rendered in the IdentityPublic section; column: professional_profiles.stage_name.
  stageName: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().max(100, 'Stage name under 100 chars'))
    .optional(),
  // Canonical, DB-mirrored handle validator (BAL-600). Empty → undefined;
  // any non-empty value is fully validated against the DB contract so an
  // invalid handle (e.g. `-2`) can never reach `normalize_and_check_handle`
  // and throw an uncaught ServiceError. See handle.schema.ts.
  handle: optionalProfileHandleSchema,
  isActive: z.boolean().default(true),
  availableForHire: z.boolean().default(true),
  isProfilePublic: z.boolean().default(false),
  // Availability detail toggle — surface the dancer's `looking_for`
  // work-type list on the public profile. Column existed pre-2026-05-13
  // but wasn't form-wired until the visibility dashboard ship.
  lookingForPublic: z.boolean().default(false).optional(),
  // BAL-248 §11b: the dancer's selected work-types (`looking_for` array,
  // `looking_for_type[]` column). The column existed + was read everywhere
  // but no editor wrote it — this is the write path. Values validated as the
  // looking_for_type enum strings; age-gating is a render-time concern
  // (availability-age-rules.ts), not a write constraint.
  lookingFor: z.array(z.string()).max(12).optional(),
  // BAL-248 §7a: featured video — references one of the dancer's OWN uploaded
  // artist_media video rows (professional_profiles.featured_video_id). The
  // wizard step + edit form restrict the choices to the dancer's uploads.
  featuredVideoId: z.string().uuid().nullable().optional(),
  // BAL-248 §15b: opt-in to surface the Agency Info section publicly.
  // Default OFF (private). Admin/super-admin bypass at render time.
  agencyInfoPublic: z.boolean().default(false).optional(),
  // Visibility toggles (added 2026-05-13 migrations 20260513120000 +
  // 20260513130000). Five per-section toggles + two per-brand opt-ins.
  // Defaults mirror DB column defaults. AND-gated against the
  // view-fields registry at render time — see
  // `docs/architecture/dancer-identity-and-visibility-model.md`.
  bioPublic: z.boolean().default(true).optional(),
  measurementsPublic: z.boolean().default(false).optional(),
  contactPublic: z.boolean().default(false).optional(),
  socialPublic: z.boolean().default(true).optional(),
  resumePublic: z.boolean().default(true).optional(),
  visibleOnBallee: z.boolean().default(true).optional(),
  visibleOnFlow: z.boolean().default(true).optional(),
  // `currentAffiliation` (Meteor-parity scalar) and `danceEducation` (JSONB
  // array) were retired in 20260426142200_drop_current_affiliation_column +
  // 20260426105624_consolidate_dance_education_into_experiences. Both
  // shipped as duplicates of the `experiences` table; the rich
  // ExperienceStep card with org search + ranks + `current` flag is the
  // single canonical surface for training/employment/competition history.
});

/**
 * Physical Attributes Schema
 * Handles body measurements and sizing information
 */
export const PhysicalAttributesSchema = z.object({
  // clearableNumber: an empty number input (NaN) becomes null so the field can
  // be blanked to clear the column, instead of failing min()/max() validation.
  heightCm: clearableNumber(
    z
      .number()
      .min(50, 'Height must be at least 50cm')
      .max(250, 'Height too tall'),
  ),
  weightKg: clearableNumber(
    z
      .number()
      .min(1, 'Weight must be at least 1kg')
      .max(200, 'Weight too high'),
  ),
  bustCm: clearableNumber(
    z
      .number()
      .min(60, 'Measurement too small (minimum 60cm)')
      .max(150, 'Measurement too large (maximum 150cm)'),
  ),
  waistCm: clearableNumber(
    z
      .number()
      .min(50, 'Measurement too small (minimum 50cm)')
      .max(120, 'Measurement too large (maximum 120cm)'),
  ),
  hipsCm: clearableNumber(
    z
      .number()
      .min(60, 'Measurement too small (minimum 60cm)')
      .max(150, 'Measurement too large (maximum 150cm)'),
  ),
  clothingSize: z.enum(['XS', 'S', 'M', 'L', 'XL', 'XXL']).optional(),
  shoeSize: z.string().max(10, 'Shoe size too long').optional(),
  // BAL-248 §15d: US shoe size alongside the existing EU `shoeSize`. Free-text
  // (US sizes can be "8.5", "W7/M5.5"). Column: artist_profiles.shoe_size_us.
  shoeSizeUs: z.string().max(10, 'Shoe size too long').optional(),
  pointeShoeBrand: z.string().max(50, 'Brand name too long').optional(),
  pointeShoeSize: z.string().max(10, 'Shoe size too long').optional(),
  // Meteor-parity additions (Phase 2c). Free-text — no enum, on purpose.
  // Architecture doc §Phase 2 documents the deliberate anti-pattern choice
  // for skin_tone.
  eyeColor: z
    .string()
    .max(50, 'Eye color too long')
    .transform((val) => val?.trim())
    .optional(),
  hairColor: z
    .string()
    .max(50, 'Hair color too long')
    .transform((val) => val?.trim())
    .optional(),
  skinTone: z
    .string()
    .max(50, 'Skin tone too long')
    .transform((val) => val?.trim())
    .optional(),
  // Identity axis (added 2026-05-12 column, surfaced 2026-05-13 in
  // 20260513130000). Self-declared identity: professional / student /
  // amateur. Gates the Professional section in the public view. Lives
  // on the physical section to align with the dancer_profiles row
  // shape (the column physically lives on artist_profiles).
  profileCategory: z.enum(['professional', 'student', 'amateur']).optional(),
});

/**
 * Geographic Availability Schema
 * Handles geographical regions where dancer is available to work
 */
export const GeographicAvailabilitySchema = z.object({
  geographicAvailability: z
    .array(z.string().max(100))
    .max(20, 'Maximum 20 locations')
    .optional(),
});

/**
 * Emergency Contact Schema
 * Handles emergency contact information
 */
export const EmergencyContactSchema = z.object({
  emergencyContactName: z.string().max(100, 'Name too long').optional(),
  emergencyContactPhone: phoneE164Field.optional(),
  emergencyContactRelationship: z
    .string()
    .max(50, 'Relationship too long')
    .optional(),
});

/**
 * Payment Information Schema
 * Handles payment method setup for contract earnings
 */
export const PaymentInfoSchema = z.object({
  accountHolderName: z
    .string()
    .min(2, 'Account holder name must be at least 2 characters')
    .max(100, 'Account holder name too long')
    .regex(/^[a-zA-ZÀ-ÿ\s\-'.]+$/, 'Invalid name format'),
  iban: z
    .string()
    .transform((val) => val.replace(/\s/g, '').toUpperCase())
    .refine((val) => {
      const result = isValidIBAN(val);
      // Handle both object and boolean return types from ibantools
      if (typeof result === 'boolean') {
        return result;
      }
      // If result is an object with a 'valid' property
      return (result as { valid: boolean })?.valid === true;
    }, 'Invalid IBAN. Please check the format and try again.'),
  bankName: z
    .string()
    .min(2, 'Bank name must be at least 2 characters')
    .max(100, 'Bank name too long')
    .optional(),
  paymentType: z.enum(['bank_account']).default('bank_account'),
  isVerified: z.boolean().default(false),
});

/**
 * Identity Verification Schema
 * Handles identity document uploads and verification for contracts
 *
 * SECURITY: verification_status is OPTIONAL and READ-ONLY in this schema.
 * - Users can READ their verification status (to display in UI)
 * - Users CANNOT SET their own verification status
 * - The database mapper excludes this field from writes
 * - Only admins can update verification status directly in database
 * - Database column default ('pending') handles initial status automatically
 */
export const IdentityVerificationSchema = z.object({
  countryCode: z
    .string()
    .length(2, 'Country code must be 2 characters')
    .regex(/^[A-Z]{2}$/, 'Invalid country code format')
    .optional(),
  documentType: z
    .enum(['passport', 'national_id', 'drivers_license'], {
      errorMap: () => ({ message: 'Please select a valid document type' }),
    })
    .optional(),
  documentNumber: z
    .string()
    .max(50, 'Document number too long')
    .regex(/^[A-Za-z0-9\-\s]+$/, 'Invalid document number format')
    .optional(),
  // Image fields store Supabase storage paths (not URLs)
  // Format: bucket-name/user-id/path/filename.ext
  // Signed URLs are generated when viewing
  frontImageUrl: z.string().optional(),
  backImageUrl: z.string().optional(),
  selfieImageUrl: z.string().optional(),
  // READ-ONLY: Can be present when loading from database, but excluded from writes
  // See database-mappers.ts - IdentityVerificationMapper excludes this field
  verificationStatus: z
    .enum(['pending', 'verified', 'rejected', 'expired'])
    .optional(),
  expiryDate: z.string().optional(),
  issueDate: z.string().optional(),
  notes: z.string().max(500, 'Notes too long').optional(),
});

/**
 * Tax Information Schema
 * Handles identification documents (ID/passport only)
 * Note: Tax ID and tax number are stored in artist_contract_details table, not profiles.
 * This schema only handles the ID/passport number field for general identification.
 */
export const TaxInfoSchema = z.object({
  idPassportNumber: z
    .string()
    .transform((val) => (val.trim() === '' ? undefined : val))
    .optional()
    .refine((val) => !val || val.length <= 50, 'ID/Passport number too long')
    .refine(
      (val) => !val || /^[A-Za-z0-9\-\s]+$/.test(val),
      'Invalid ID/Passport number format',
    ),
});

/**
 * Main Dancer Form Schema
 * Combines all sub-schemas into a complete form validation schema
 */
export const DancerFormSchema = z.object({
  personal: PersonalInfoSchema,
  address: AddressSchema,
  professional: ProfessionalInfoSchema,
  physical: PhysicalAttributesSchema,
  geographic: GeographicAvailabilitySchema,
  emergency: EmergencyContactSchema,
  tax: TaxInfoSchema,
  identity: IdentityVerificationSchema,
  notes: z.string().optional(),
});

// Partial schema for updates/autosave - allows partial sections and partial fields
export const DancerFormUpdateSchema = z.object({
  personal: PersonalInfoSchema.partial().optional(),
  address: AddressSchema.partial().optional(),
  professional: ProfessionalInfoSchema.partial().optional(),
  physical: PhysicalAttributesSchema.partial().optional(),
  geographic: GeographicAvailabilitySchema.partial().optional(),
  emergency: EmergencyContactSchema.partial().optional(),
  tax: TaxInfoSchema.partial().optional(),
  identity: IdentityVerificationSchema.partial().optional(),
  notes: z.string().optional(),
});

// Type inference for TypeScript
export type DancerFormInput = z.infer<typeof DancerFormSchema>;
export type DancerFormUpdateInput = z.infer<typeof DancerFormUpdateSchema>;
export type PersonalInfo = z.infer<typeof PersonalInfoSchema>;
export type AddressInfo = z.infer<typeof AddressSchema>;
export type ProfessionalInfo = z.infer<typeof ProfessionalInfoSchema>;
export type PhysicalAttributes = z.infer<typeof PhysicalAttributesSchema>;
export type GeographicAvailability = z.infer<
  typeof GeographicAvailabilitySchema
>;
export type EmergencyContact = z.infer<typeof EmergencyContactSchema>;
export type TaxInfo = z.infer<typeof TaxInfoSchema>;
export type IdentityVerification = z.infer<typeof IdentityVerificationSchema>;

/**
 * Partial schemas for individual form sections
 * Useful for step-by-step validation in multi-step forms
 */
export const DancerFormSectionSchemas = {
  personal: PersonalInfoSchema,
  address: AddressSchema,
  professional: ProfessionalInfoSchema,
  physical: PhysicalAttributesSchema,
  geographic: GeographicAvailabilitySchema,
  emergency: EmergencyContactSchema,
  tax: TaxInfoSchema,
  identity: IdentityVerificationSchema,
  payment: PaymentInfoSchema,
} as const;

/**
 * Combined Contract Verification Schema
 * Includes both identity verification and payment setup
 */
export const ContractVerificationSchema = z.object({
  identity: IdentityVerificationSchema,
  payment: PaymentInfoSchema,
});

// TypeScript types derived from schemas
export type PaymentInfo = z.infer<typeof PaymentInfoSchema>;
export type ContractVerification = z.infer<typeof ContractVerificationSchema>;
